import React from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip'
import { motion } from 'framer-motion'

// Animation variants for the icons
const iconVariants = {
  initial: {
    scale: 1,
    rotate: 0,
    y: 0,
  },
  hover: {
    scale: [1, 1.2, 1.1],
    rotate: [0, -15, 15, -5, 0],
    y: [0, -5, 0],
    transition: {
      duration: 0.6,
      rotate: { duration: 0.6 },
      scale: { times: [0, 0.5, 1], duration: 0.6 },
      y: { times: [0, 0.5, 1], duration: 0.6 },
    },
  },
  active: {
    scale: [1.1, 1.3, 1.2],
    rotate: 0,
    y: [0, -3, 0],
  },
  activeHover: {
    scale: [1.1, 1.3, 1.2],
    rotate: [0, -10, 10, -5, 0],
    y: [0, -3, 0],
    transition: {
      duration: 0.5,
      rotate: { duration: 0.5 },
      scale: { times: [0, 0.5, 1], duration: 0.5 },
      y: { times: [0, 0.5, 1], duration: 0.5 },
    },
  },
}

// Animation variants for the menu items
const menuItemVariants = {
  initial: {
    x: 0,
    y: 0,
    scale: 1,
  },
  hover: {
    x: 4,
    y: 0,
    scale: 1,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
  active: {
    x: 0,
    y: -2,
    scale: 1.02,
    transition: { duration: 0.2 },
  },
  activeHover: {
    x: 4,
    y: -3,
    scale: 1.03,
    transition: { duration: 0.2 },
  },
}

const SidebarItem = React.memo(({ item, isActive, sideMenuVisible }) => {
  const { t } = useTranslation('common')
  const href = item.query ? `/${item.route}?${item.query}=${item.queryValue}` : `/${item.route}`
  const Icon = item.icon

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <motion.div
          initial="initial"
          animate={isActive ? 'active' : 'initial'}
          whileHover={isActive ? 'activeHover' : 'hover'}
          variants={menuItemVariants}
          className="w-full"
        >
          <Link
            href={href}
            className={cn(
              'flex relative items-center w-full hover:bg-gray-50 hover:text-black rounded-lg transition-all duration-200 group',
              'text-sm',
              isActive ? 'text-black bg-gray-50  ' : 'text-gray-600 ',
              sideMenuVisible ? 'px-4 py-3' : 'justify-center p-3'
            )}
          >
            <motion.div
              variants={iconVariants}
              className={cn(
                'flex justify-center items-center rounded-md transition-colors',
                sideMenuVisible ? 'mr-3' : ''
              )}
            >
              <Icon
                size={20}
                strokeWidth={isActive ? 2.5 : 1.8}
                className={cn('transition-all duration-200', isActive ? 'text-black' : 'text-gray-500')}
              />
            </motion.div>
            {sideMenuVisible && (
              <span className={cn('truncate', isActive ? 'font-semibold' : '')}>
                {t(`pages.${item.key || item.route}`, item.label)}
              </span>
            )}
          </Link>
        </motion.div>
      </TooltipTrigger>
      {!sideMenuVisible && (
        <TooltipContent
          side="right"
          className="bg-white text-gray-800 border border-gray-200 py-1 px-2 shadow-lg z-[999]"
        >
          {t(`pages.${item.key || item.route}`, item.label)}
        </TooltipContent>
      )}
    </Tooltip>
  )
})

export { SidebarItem }
